import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import {
	type ActionFunctionArgs,
	redirect,
	useLoaderData,
	type MetaFunction,
	Link,
} from 'react-router'
import rehypeExternalLinks from 'rehype-external-links'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import { serverOnly$ } from 'vite-env-only/macros'
import PageTitle from '#app/components/data-display/PageTitle.tsx'
import {
	type Heading,
	TableOfContents,
} from '#app/components/entities/commonElements/TableOfContents.tsx'
import AdminOffices from '#app/components/entities/procedures/AdminOffices.tsx'
import ProcedureFAQs from '#app/components/entities/procedures/ProcedureFAQs.tsx'
import ProcedureQuickInfo from '#app/components/entities/procedures/ProcedureQuickInfo.tsx'
import ProcedureRelatedProcedures from '#app/components/entities/procedures/ProcedureRelatedProcedures.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Breadcrumb } from '#app/components/navigation/Breadcrumb.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import adminPreferences from '#app/config/adminPreferences.json'
import {
	getProcedureBySlug,
	getProceduresForSitemap,
} from '#app/routes/_appAdmin+/procedures/_proceduresQueries.server.ts'
import { getRedirection } from '#app/routes/_appAdmin+/redirections/_redirectionsQueries.server.ts'
import { insertAdsInContent } from '#app/utils/pz/ezoic.tsx'
import { createFAQSchemaFromData } from '#app/utils/pz/faq.ts'
import { formatDate } from '#app/utils/pz/formatDate.ts'
import { extractHeadingsFromMarkdown } from '#app/utils/toc.ts'

export async function action({ request }: ActionFunctionArgs) {
	throw {
		error: 'Unauthorized',
		message: `Unauthorized action`,
	}
}

export async function loader({ params, request }: DataFunctionArgs) {
	const procedure = await getProcedureBySlug(String(params.procedureId))

	if (!procedure) {
		const url = new URL(request.url)

		const redirection = await getRedirection(url.pathname)
		if (redirection) {
			return redirect(redirection.newUrl)
		}
	}
	invariantResponse(procedure, 'Este trámite no existe en Papelea', {
		status: 404,
	})
	invariantResponse(
		procedure.administration.slug === params.administration,
		'Este trámite no existe en Papelea',
		{ status: 404 },
	)
	invariantResponse(
		procedure.administration.country.code === params.country,
		'Este trámite no existe en Papelea',
		{ status: 404 },
	)

	return {
		procedure,
	}
}

export interface FAQData {
	question: string
	answer: string
}

export default function ProcedureRoute() {
	const data = useLoaderData<typeof loader>()
	const { procedure } = data

	const pages = [
		{
			name: `Administraciones de ${procedure.administration.country.name}`,
			href: `/${procedure.administration.country.code}/administraciones`,
			current: false,
		},
		{
			name: procedure.administration.name,
			href: `/${procedure.administration.country.code}/${procedure.administration.slug}`,
			current: false,
		},
		{
			name:
				procedure.name.slice(0, 50) + (procedure.name.length > 50 ? '...' : ''),
			href: '#',
			current: true,
		},
	]

	const updatedDate = formatDate(procedure.updatedAt)

	const isHtmlProcedure = procedure.lastOptimized == null
	const quickInfo: Boolean = procedure.quickInfo != null || false
	const faqs: FAQData[] = JSON.parse(procedure?.faqs || '[]') as FAQData[]

	const { faqsExist, faqSchema } = createFAQSchemaFromData(faqs)
	const adminOffices: Boolean = procedure.administration.Offices.length > 0
	const relatedProcedures: Boolean = procedure.Procedure_A.length > 0
	const toc = isHtmlProcedure
		? procedure.toc
			? (JSON.parse(procedure.toc) as Heading[])
			: []
		: extractHeadingsFromMarkdown(procedure.content)

	const [updatedContent, setUpdatedContent] = useState(procedure.content)

	useEffect(() => {
		const updatedHtmlContent = insertAdsInContent(procedure.content)
		setUpdatedContent(updatedHtmlContent)
	}, [procedure.content])

	useEffect(() => {
		// Initialize Ezoic ads
		const script = document.createElement('script')
		script.src = '//www.ezojs.com/ezoic/sa.min.js'
		script.async = true
		document.head.appendChild(script)

		script.onload = () => {
			window.ezstandalone?.enable()
			window.ezstandalone?.display()
		}

		return () => {
			document.head.removeChild(script)
		}
	}, [])

	return (
		<>
			<main className="relative pt-12 lg:grid lg:grid-cols-[1fr_300px] lg:gap-10 xl:gap-20">
				<div>
					<Breadcrumb items={pages} />
					<PageTitle title={procedure.name} />
					<div>
						<Badge
							className="rounded-sm text-gray-700"
							variant="secondary"
						>{`Actualizado el ${updatedDate}`}</Badge>
						<span className="pl-4 text-sm">
							Ver en la web de la{' '}
							<a
								className="text-amber-700 hover:font-semibold hover:text-amber-400"
								href={procedure.admProcedureUrl}
								target="_blank"
								rel="noreferrer"
							>
								administración
							</a>
						</span>
					</div>
					{quickInfo && (
						<ProcedureQuickInfo quickInfo={JSON.parse(procedure.quickInfo)} />
					)}
					{isHtmlProcedure ? (
						<div
							className="prose prose-a:font-semibold prose-a:text-amber-700 prose-a:no-underline prose-a:hover:text-amber-400 mt-6 max-w-prose"
							dangerouslySetInnerHTML={{ __html: updatedContent }}
						/>
					) : (
						<div className="prose prose-a:font-semibold prose-a:text-amber-700 prose-a:no-underline prose-a:hover:text-amber-400 mt-6 max-w-prose">
							<ReactMarkdown
								rehypePlugins={[
									rehypeSlug,
									rehypeRaw, // Enables rendering of raw HTML
									[
										rehypeExternalLinks,
										{ target: '_blank', rel: 'noopener noreferrer' },
									], // Properly configured external links
								]}
								components={{
									a: ({ href, children }) => {
										const isExternal = href?.startsWith('http')
										return (
											<a
												href={href}
												target={isExternal ? '_blank' : undefined}
												rel={isExternal ? 'noopener noreferrer' : undefined}
											>
												{children}
											</a>
										)
									},
								}}
							>
								{procedure.content}
							</ReactMarkdown>
						</div>
					)}

					{faqsExist && <ProcedureFAQs faqData={faqs} faqSchema={faqSchema} />}
					{relatedProcedures && (
						<ProcedureRelatedProcedures
							relatedProcedures={procedure?.Procedure_A}
						/>
					)}

					{adminOffices && (
						<>
							<hr className="mt-8" />
							<div className="prose my-8">
								<h2 id="oficinas-administracion">
									Oficinas de la administración
								</h2>
							</div>
							<AdminOffices offices={procedure.administration.Offices} />
							<div className="mt-4">
								<Link
									reloadDocument
									to={`../${procedure.administration.country.code}/${procedure.administration.slug}?tab=oficinas`}
									className="font-bold text-amber-700 hover:font-semibold hover:text-amber-400"
								>
									Ver más oficinas
								</Link>
							</div>
						</>
					)}
				</div>
				<div className="hidden text-sm lg:block">
					<div className="sticky top-16 -mt-10 max-h-[calc(var(--vh)-4rem)] overflow-y-auto pt-10">
						<TableOfContents
							headings={toc}
							otherSections={[
								faqsExist && {
									id: 'preguntas-frecuentes',
									text: 'Preguntas frecuentes',
									heading: 'h2',
								},
								relatedProcedures && {
									id: 'tramites-relacionados',
									text: 'Trámites relacionados',
									heading: 'h2',
								},
								adminOffices && {
									id: 'oficinas-administracion',
									text: 'Oficinas de la administración',
									heading: 'h2',
								},
							]}
						/>
					</div>
				</div>
			</main>
		</>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const procedureAdministration =
		data?.procedure.administration.name ?? 'Papelea'
	const procedureName = data?.procedure.name ?? 'Trámite'
	const procedureSummary = data
		? data.procedure.content
				.replace(/#{1,6}\s+([\s\S]+?)\s*\n+/g, '$1: ')
				.replace(/\n+/g, ' ')
				.slice(0, 150) + '...'
		: 'No hay contenido'
	const metaTitle = `${procedureName} | ${procedureAdministration} `

	const governmentServiceSchema = {
		'@context': 'https://schema.org',
		'@type': 'GovernmentService',
		name: data?.procedure.name,
		description: procedureSummary,
		serviceType: `Servicios de: ${data?.procedure.administration.name}`,
		serviceOperator: {
			'@type': 'GovernmentOrganization',
			name: data?.procedure.administration.name,
			areaServed: data?.procedure.administration.country.name,
		},
		url: `${adminPreferences.baseUrl}/${params.country}/${params.administration}/${params.procedureId}`,
	}

	const baseMetaTags = [
		{ title: metaTitle },
		{
			tagName: 'link',
			rel: 'canonical',
			href: `${adminPreferences.baseUrl}/${params.country}/${params.administration}/${params.procedureId}`,
		},
		{
			name: 'description',
			content: procedureSummary,
		},
		{
			name: 'keywords',
			content:
				metaTitle +
				',' +
				procedureAdministration +
				', trámite, administraciones',
		},
		{
			property: 'robots',
			content: 'index, follow',
		},
		{
			property: 'og:type',
			content: 'website',
		},
		{
			property: 'og:url',
			content: `${adminPreferences.baseUrl}/${params.country}/${params.administration}/${params.procedureId}`,
		},
		{
			property: 'og:title',
			content: metaTitle,
		},
		{
			name: 'og:description',
			content: procedureSummary,
		},
		{
			'script:ld+json': governmentServiceSchema,
		},
	]

	return baseMetaTags
}

export const handle: SEOHandle = serverOnly$({
	getSitemapEntries: async (request) => {
		const procedures = await getProceduresForSitemap()
		return procedures.map((procedure) => ({
			route: `/${procedure.administration.country.code}/${procedure.administration.slug}/${procedure.slug}`,
			priority: 0.7,
		}))
	},
})

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
